import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import Button, { LoadingButton } from '@atlaskit/button';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { InProductHelpLink } from '@atlassian/jira-servicedesk-common/src/ui/components/in-product-help-link';
import { useConfluencePermission } from '@atlassian/jira-servicedesk-post-incident-review-confluence-permission';
import { SettingsCardComponent } from '@atlassian/jira-servicedesk-settings-common/src/ui/settings-card-component';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { settingsCardComponentStyles } from '../../common/ui';
import { SectionTitle } from '../../common/ui/section-title';
import { usePIRConfiguration } from '../../services/main.tsx';
import { PostIncidentReviewConfluenceSection } from './confluence';
import messages from './messages';
import { PostIncidentReviewTemplatesSection } from './templates';

export const PostIncidentReviewSection = () => {
	const { formatMessage } = useIntl();
	const { cloudId } = useTenantContext();
	const { showFlag } = useFlagsService();
	const { data: projectContext } = useProjectContext();
	const { result: confluencePermission, error } = useConfluencePermission(cloudId);
	const [{ pirConfigurationData = {} }, { setPIRConfiguration, resetSelectedConfluenceConfig }] =
		usePIRConfiguration();

	// @ts-expect-error - TS2339 - Property 'isSaving' does not exist on type '{}'. | TS2339 - Property 'selectedConfluenceSpaceId' does not exist on type '{}'. | TS2339 - Property 'selectedConfluenceParentPageId' does not exist on type '{}'.
	const { isSaving, selectedConfluenceSpaceId, selectedConfluenceParentPageId, isEditEnabled } =
		pirConfigurationData;
	const projectId = projectContext ? toProjectId(projectContext.projectId.toString()) : undefined;
	const displayConfluenceConfiguration = confluencePermission.userHasConfluenceLicense;

	useEffect(() => {
		if (error) {
			showFlag({
				type: 'error',
				title: messages.errorTitle,
				description: messages.errorDescription,
			});
		}
	}, [error, showFlag]);

	return (
		<>
			<SettingsCardComponent
				title={
					ff('im_feature_enablement_gating') ? (
						<Flex alignItems="start">
							<SectionTitle
								icon={<CopyIcon label="" />}
								message={formatMessage(messages.pirSectionHeading)}
							/>
							<Box xcss={premiumBadgeStyles}>
								<Lozenge appearance="new">{formatMessage(messages.premiumLozenge)}</Lozenge>
							</Box>
						</Flex>
					) : (
						<SectionTitle
							icon={<CopyIcon label="" />}
							message={formatMessage(messages.pirSectionHeading)}
						/>
					)
				}
				styles={settingsCardComponentStyles}
				footerActionItems={
					displayConfluenceConfiguration && isEditEnabled
						? [
								<LoadingButton
									key="savePIRConfigButton"
									appearance="primary"
									isLoading={isSaving}
									isDisabled={
										isSaving ||
										selectedConfluenceSpaceId === undefined ||
										selectedConfluenceParentPageId === undefined
									}
									onClick={(e, analyticsEvent) => {
										setPIRConfiguration({
											cloudId,
											projectId,
											analyticsEvent,
										});
										fireUIAnalytics(analyticsEvent, 'savePIRConfigButton');
									}}
								>
									{formatMessage(messages.pirSaveChangesButtonText)}
								</LoadingButton>,
								<Button
									key="discardPIRConfigChangesButton"
									data-test-id="servicedesk-incident-management-settings.ui.post-incident-reviews.incident-management-settings-pir-discard-btn"
									appearance="subtle"
									onClick={(e, analyticsEvent) => {
										resetSelectedConfluenceConfig();
										fireUIAnalytics(analyticsEvent, 'discardPIRConfigChangesButton');
									}}
								>
									{formatMessage(messages.pirDiscardChangesButtonText)}
								</Button>,
							]
						: []
				}
			>
				<Box xcss={[sectionParagraphStyles, fieldDescriptionStyles]}>
					<span>
						{formatMessage(messages.pirSectionDescription)}
						<InProductHelpLink
							href="https://support.atlassian.com/jira-service-management-cloud/docs/what-are-post-incident-reviews/"
							learnMoreText={formatMessage(messages.pirLearnMoreLink)}
							inProductHelpArticleId="3e1FLCRCpBoQjPXeBydIx5"
						/>
					</span>
					&nbsp;
				</Box>
				<SubSections>
					<PostIncidentReviewTemplatesSection />
				</SubSections>

				{displayConfluenceConfiguration && (
					<SubSections>
						<PostIncidentReviewConfluenceSection />
					</SubSections>
				)}
			</SettingsCardComponent>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubSections = styled.div({
	marginTop: token('space.400', '32px'),
});
const premiumBadgeStyles = xcss({ marginTop: 'space.025' });

const sectionParagraphStyles: ReturnType<typeof xcss> = xcss({
	color: 'color.text.subtle',
	marginTop: 'space.200',
	marginBottom: 'space.400',
});

const fieldDescriptionStyles: ReturnType<typeof xcss> = xcss({
	marginBottom: 'space.250',
});
